import { session } from 'auth/session'
import { useOrgName } from 'common/hooks/useOrgName'
import { useOrgs } from 'common/hooks/useOrgs'
import { NoAccessPanel } from 'settings/NoAccessPanel'

const me = session.get('user')

/**
 * Gate for org-settings pages restricted to owners/admins (e.g. License Keys,
 * Billing). Collaborators get a NoAccessPanel in place of the page — the real
 * page never mounts, so no data is fetched (the backend enforces roles too).
 * Renders nothing until member roles load, so owners/admins don't flash the
 * panel while the request is in flight.
 *
 * Reuses useOrgs (the shared core/tenants fetch + loading state) rather than
 * re-implementing the fetch, so the role source stays in one place.
 */
export const RequireOrgAdmin = ({ feature, children }) => {
  const currentOrgName = useOrgName()
  const { orgs, isLoadingOrgs, errorOrgs } = useOrgs()

  if (me?.superAdmin) {
    return children
  }

  if (isLoadingOrgs || !currentOrgName) {
    return null
  }

  // Fail open ONLY when the fetch errored with nothing cached — don't lock
  // owners/admins out (SWR has shouldRetryOnError: false). If orgs are cached
  // (SWR keeps prior data even when a background revalidation errors), fall
  // through and use them, so a known collaborator still gets the panel.
  if (errorOrgs && !orgs) {
    return children
  }

  const canModifyOrg = orgs?.find(
    (org) => org.orgName === currentOrgName && (org.role === 'owner' || org.role === 'admin')
  )

  if (!canModifyOrg) {
    return <NoAccessPanel feature={feature} />
  }

  return children
}
